import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Upload, Mic, Sparkles, Linkedin, Twitter, Instagram, Youtube,
  Clock, TrendingUp, Search, Target, CheckCircle2, X, Loader2, Zap,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Scene timing (seconds, cumulative).                                 */
/* Total ~28s, then it loops.                                          */
/* ------------------------------------------------------------------ */
const SCENES = [
  { id: 'pain',     start: 0,    duration: 4.0, label: 'Then vs Now' },
  { id: 'upload',   start: 4.0,  duration: 3.5, label: 'Drop one clip' },
  { id: 'scribe',   start: 7.5,  duration: 5.5, label: 'We transcribe + surface hooks' },
  { id: 'preset',   start: 13.0, duration: 3.5, label: 'Pick your voice' },
  { id: 'output',   start: 16.5, duration: 7.5, label: '4 platforms · SEO + conversion-tuned' },
  { id: 'savings',  start: 24.0, duration: 4.0, label: 'From "where do I start?" to scheduled' },
];
const TOTAL = SCENES.reduce((s, x) => Math.max(s, x.start + x.duration), 0);

const sceneActive = (id, t) => {
  const s = SCENES.find((x) => x.id === id);
  return t >= s.start && t < s.start + s.duration;
};
const sceneProgress = (id, t) => {
  const s = SCENES.find((x) => x.id === id);
  return Math.max(0, Math.min(1, (t - s.start) / s.duration));
};

/* ============== SCENE 1 — PAIN ============== */
const PainScene = ({ p }) => {
  const seconds = Math.floor(p * 47); // counts up to 47 sec
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8" data-testid="demo-scene-pain">
      <div className="text-center max-w-md">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-xs mb-6">
          <Clock className="w-3.5 h-3.5" /> Then vs Now
        </div>
        <div className="font-heading text-2xl sm:text-4xl font-bold text-zinc-100 leading-tight mb-6">
          Shooting your clip took an hour.
          <br />
          <span className="text-zinc-500">Captioning it took…</span>
        </div>
        <div className="font-display text-7xl sm:text-8xl font-bold tabular-nums bg-gradient-to-r from-emerald-300 to-emerald-500 bg-clip-text text-transparent">
          {seconds}
          <span className="text-2xl text-emerald-300/70 font-sans font-medium ml-1">sec</span>
        </div>
        <div className="mt-4 text-sm text-zinc-400">
          Across LinkedIn · X · Instagram · YouTube — done.
        </div>
      </div>
    </div>
  );
};

/* ============== SCENE 2 — UPLOAD ============== */
const UploadScene = ({ p }) => {
  const progress = Math.min(100, Math.floor(p * 110));
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8" data-testid="demo-scene-upload">
      <div className="w-full max-w-lg">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-xs mb-5">
          <Upload className="w-3.5 h-3.5" /> Step 1
        </div>
        <div
          className={`rounded-2xl border-2 border-dashed transition-all duration-700 p-8 ${
            p < 0.25
              ? 'border-zinc-700 bg-zinc-900/40'
              : 'border-indigo-500/60 bg-indigo-500/5 scale-[1.01]'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 shadow-lg shadow-indigo-500/30">
              <Upload className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-zinc-100 truncate">launch_teaser_v3.mp4</div>
              <div className="text-xs text-zinc-500">32.4 MB · 00:47</div>
              <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-[width] duration-200 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <div className="text-xs text-zinc-400 tabular-nums shrink-0 w-10 text-right">{progress}%</div>
          </div>
        </div>
        <div className="mt-5 text-center text-sm text-zinc-500">
          Drag-drop, batch, up to 500 MB. We handle the rest.
        </div>
      </div>
    </div>
  );
};

/* ============== SCENE 3 — TRANSCRIBE ============== */
const TRANSCRIPT_LINES = [
  { t: 'We rebuilt our onboarding around one metric —', highlight: false },
  { t: 'and watched activation jump 41% in 14 days.', highlight: true },
  { t: 'Most teams ship features. Great teams ship outcomes.', highlight: false },
  { t: "Here's what we cut, and why it worked.", highlight: false },
];

const TranscribeScene = ({ p }) => {
  const visibleLines = Math.floor(p * (TRANSCRIPT_LINES.length + 0.5));
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8" data-testid="demo-scene-transcribe">
      <div className="w-full max-w-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs mb-5">
          <Mic className="w-3.5 h-3.5" /> Step 2 · Whisper transcribing
        </div>
        <div className="rounded-2xl border border-white/10 bg-zinc-900/70 backdrop-blur-md p-6">
          <div className="flex items-center gap-2 mb-4 text-xs text-zinc-500 font-mono">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />
            transcribing… surfacing quote-worthy hooks
          </div>
          <div className="space-y-2.5">
            {TRANSCRIPT_LINES.map((line, i) => {
              const shown = i < visibleLines;
              return (
                <div
                  key={i}
                  className={`text-base sm:text-lg leading-relaxed transition-all duration-500 ${
                    shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                  } ${
                    line.highlight && shown
                      ? 'bg-yellow-400/15 border-l-2 border-yellow-400 pl-3 text-yellow-100 font-medium rounded-r'
                      : 'text-zinc-300'
                  }`}
                >
                  {line.t}
                  {line.highlight && shown && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-yellow-400/80">
                      <Sparkles className="w-3 h-3" /> hook
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============== SCENE 4 — PRESET ============== */
const PRESETS = [
  { Icon: Linkedin, name: 'LinkedIn · Founder', tag: 'Bold · Contrarian', color: 'from-sky-500/30 to-blue-600/20', accent: 'border-sky-500/40' },
  { Icon: Twitter, name: 'X · Punchy', tag: 'Pattern interrupt', color: 'from-zinc-500/30 to-zinc-700/20', accent: 'border-zinc-400/40' },
  { Icon: Instagram, name: 'Instagram · Caption', tag: 'Save-bait CTA', color: 'from-pink-500/30 to-fuchsia-600/20', accent: 'border-pink-500/40' },
  { Icon: Youtube, name: 'YouTube · SEO desc', tag: 'Keyword-aware', color: 'from-red-500/30 to-rose-600/20', accent: 'border-red-500/40' },
];

const PresetScene = ({ p }) => (
  <div className="absolute inset-0 flex items-center justify-center p-8" data-testid="demo-scene-preset">
    <div className="w-full max-w-2xl">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs mb-5">
        <Sparkles className="w-3.5 h-3.5" /> Step 3 · Pick presets (or build one in plain English)
      </div>
      <div className="grid grid-cols-2 gap-3">
        {PRESETS.map((preset, i) => {
          const appearAt = i * 0.18;
          const visible = p > appearAt;
          const Icon = preset.Icon;
          return (
            <div
              key={preset.name}
              className={`rounded-xl border ${preset.accent} bg-gradient-to-br ${preset.color} backdrop-blur-md p-4 transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-3 scale-95'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className="w-5 h-5 text-white" />
                <CheckCircle2
                  className={`w-4 h-4 text-emerald-400 transition-opacity duration-300 ${
                    p > appearAt + 0.2 ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </div>
              <div className="text-sm font-medium text-zinc-100">{preset.name}</div>
              <div className="text-xs text-zinc-400 mt-0.5">{preset.tag}</div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

/* ============== SCENE 5 — OUTPUT ============== */
const OUTPUTS = [
  {
    Icon: Linkedin,
    platform: 'LinkedIn',
    benefit: '3× impressions on hook-led posts',
    BenefitIcon: TrendingUp,
    body: 'Most teams ship features. Great teams ship outcomes.\n\nWe rebuilt onboarding around one metric — activation jumped 41% in 14 days. Here\'s what we cut →',
    color: 'border-sky-500/30 bg-sky-500/5',
  },
  {
    Icon: Twitter,
    platform: 'X',
    benefit: 'Pattern-interrupt opener → reposts',
    BenefitIcon: Zap,
    body: 'Cut onboarding in half.\nActivation up 41%.\n\nEvery extra step is a tax on belief.',
    color: 'border-zinc-400/30 bg-zinc-400/5',
  },
  {
    Icon: Instagram,
    platform: 'Instagram',
    benefit: 'Save-bait CTA · higher reach',
    BenefitIcon: Target,
    body: 'Built. Cut. Rebuilt.\nActivation up 41% — we removed 6 steps doing it.\n\nSave this if you\'re mid-launch ↓',
    color: 'border-pink-500/30 bg-pink-500/5',
  },
  {
    Icon: Youtube,
    platform: 'YouTube',
    benefit: 'SEO keywords baked in',
    BenefitIcon: Search,
    body: 'In this clip: how we redesigned onboarding around one north-star metric, what we removed, and the 41% activation lift it produced.\n\nKeywords: SaaS onboarding, activation rate, product-led growth',
    color: 'border-red-500/30 bg-red-500/5',
  },
];

const OutputScene = ({ p }) => (
  <div className="absolute inset-0 flex items-center justify-center p-6 sm:p-8" data-testid="demo-scene-output">
    <div className="w-full max-w-3xl">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs mb-4">
        <Sparkles className="w-3.5 h-3.5" /> Step 4 · 4 platforms · SEO + conversion-tuned
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {OUTPUTS.map((o, i) => {
          const appearAt = i * 0.15;
          const visible = p > appearAt;
          const Icon = o.Icon;
          const BenefitIcon = o.BenefitIcon;
          return (
            <div
              key={o.platform}
              className={`rounded-xl border p-4 transition-all duration-700 ${o.color} ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-zinc-200" />
                  <span className="text-xs font-medium text-zinc-200">{o.platform}</span>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-emerald-300">
                  <BenefitIcon className="w-3 h-3" />
                  {o.benefit}
                </div>
              </div>
              <div className="text-[11px] sm:text-xs text-zinc-300 leading-relaxed whitespace-pre-line">
                {o.body}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

/* ============== SCENE 6 — SAVINGS ============== */
const SavingsScene = ({ p }) => {
  const minutes = Math.floor(p * 78);
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8" data-testid="demo-scene-savings">
      <div className="text-center max-w-lg">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs mb-6">
          <TrendingUp className="w-3.5 h-3.5" /> Time saved · per clip
        </div>
        <div className="font-display text-7xl sm:text-9xl font-bold tabular-nums bg-gradient-to-r from-emerald-300 to-emerald-500 bg-clip-text text-transparent leading-none">
          –{minutes}<span className="text-3xl text-emerald-300/70 font-sans font-medium ml-1">min</span>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 max-w-md mx-auto text-left">
          <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Posts ready</div>
            <div className="font-heading text-2xl text-zinc-100">4 / 4</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">In your voice</div>
            <div className="font-heading text-2xl text-emerald-300 flex items-center gap-1">
              <CheckCircle2 className="w-5 h-5" /> yes
            </div>
          </div>
        </div>
        <div className="mt-6 text-sm text-zinc-400">From "where do I start?" to scheduled — in one tab.</div>
      </div>
    </div>
  );
};

/* ============== MAIN MODAL ============== */
export const DemoModal = ({ open, onClose, onCTA }) => {
  const [t, setT] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  // Animation loop
  useEffect(() => {
    if (!open) {
      setT(0);
      startRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ((ts - startRef.current) / 1000) % TOTAL;
      setT(elapsed);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [open]);

  const currentScene = SCENES.find((s) => t >= s.start && t < s.start + s.duration) || SCENES[0];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-5xl p-0 overflow-hidden bg-zinc-950 border-white/10 rounded-2xl"
        data-testid="demo-modal"
      >
        {/* Custom close (DialogContent already has one, but keep clean) */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-30 w-8 h-8 rounded-full bg-zinc-900/80 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
          aria-label="Close demo"
          data-testid="demo-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Stage */}
        <div className="relative w-full" style={{ aspectRatio: '16 / 9', maxHeight: '70vh' }}>
          {/* Atmosphere */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/15 via-zinc-950 to-purple-600/15" />
          <div className="absolute -top-20 -left-20 w-[40rem] h-[40rem] rounded-full bg-indigo-600/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -right-20 w-[40rem] h-[40rem] rounded-full bg-purple-600/20 blur-3xl pointer-events-none" />

          {/* Scene transitions — fade between */}
          <div className={`transition-opacity duration-500 ${sceneActive('pain', t) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {sceneActive('pain', t) && <PainScene p={sceneProgress('pain', t)} />}
          </div>
          <div className={`transition-opacity duration-500 ${sceneActive('upload', t) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {sceneActive('upload', t) && <UploadScene p={sceneProgress('upload', t)} />}
          </div>
          <div className={`transition-opacity duration-500 ${sceneActive('scribe', t) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {sceneActive('scribe', t) && <TranscribeScene p={sceneProgress('scribe', t)} />}
          </div>
          <div className={`transition-opacity duration-500 ${sceneActive('preset', t) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {sceneActive('preset', t) && <PresetScene p={sceneProgress('preset', t)} />}
          </div>
          <div className={`transition-opacity duration-500 ${sceneActive('output', t) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {sceneActive('output', t) && <OutputScene p={sceneProgress('output', t)} />}
          </div>
          <div className={`transition-opacity duration-500 ${sceneActive('savings', t) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {sceneActive('savings', t) && <SavingsScene p={sceneProgress('savings', t)} />}
          </div>

          {/* Bottom bar: timeline + scene label + CTA */}
          <div className="absolute bottom-0 left-0 right-0 px-5 sm:px-7 py-4 border-t border-white/5 bg-zinc-950/80 backdrop-blur-md flex items-center gap-4">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              {SCENES.map((s) => {
                const active = t >= s.start;
                const width = Math.max(0, Math.min(1, (t - s.start) / s.duration));
                return (
                  <div key={s.id} className="flex-1 h-1 rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-400 to-purple-400 transition-[width] duration-200 ease-linear"
                      style={{ width: active ? `${width * 100}%` : '0%' }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="hidden sm:block text-xs text-zinc-400 font-medium min-w-fit">
              {currentScene.label}
            </div>
            <button
              onClick={onCTA}
              className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-zinc-950 hover:bg-zinc-200 text-sm font-medium transition-colors"
              data-testid="demo-modal-cta"
            >
              Try it free
              <Sparkles className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
